import type { MailboxInfo } from 'owa-client-types';
import type TokenResponse from 'owa-service/lib/contract/TokenResponse';
import { getTypeHintFromMailboxInfo } from './getTypeHintFromMailboxInfo';
import { lazyLoginUserMsal } from './lazyFunctions';
import { InteractionType } from './utils/InteractionType';
import { getClaimsFromHeader } from 'owa-tokenprovider-utils';
import type {
    AtLeastOne,
    AuthParam,
    AuthenticationTargets,
    promptForResourceTokenFunc,
} from 'owa-auth-callbacks';

/**
 * Prompt for an access token for the resource. Note this may not prompt the user if the token silently acquired, but caller should expect an interactive UI.
 * @param action. A helpful string, like the one passed to makeServiceRequest, to help identify the context of the request.
 * @param mailboxInfo. Mailbox context to use for this resource token request.
 * @param targets. At least one of three parameters must be provided: resource, scope, or wwwAuthenticateHeader.
 * @param targetWindow. Window to parent the popup to. Defaults to the global window.
 */

export const promptForResourceToken: promptForResourceTokenFunc = async function (
    action: string,
    mailboxInfo: MailboxInfo,
    targets: AtLeastOne<AuthenticationTargets>,
    _additionalParameters?: AuthParam[],
    targetWindow?: Window
): Promise<TokenResponse | undefined> {
    if (!action) {
        throw new Error('Action must be provided for proper attribution.');
    }

    const claimChallenge = getClaimsFromHeader(targets.wwwAuthenticateHeader);

    return lazyLoginUserMsal.importAndExecute(
        getTypeHintFromMailboxInfo(mailboxInfo),
        InteractionType.Popup,
        undefined /* msalAccount */,
        mailboxInfo,
        undefined /* username */,
        targets.resource,
        targets.scope,
        undefined /* promptValue */,
        claimChallenge ? atob(claimChallenge) : undefined,
        undefined /* correlationId */,
        'ResourceToken',
        targetWindow
    );
};
